import DOMPurify from 'isomorphic-dompurify';
import parse, { attributesToProps, domToReact, type DOMNode, type Element, type HTMLReactParserOptions } from 'html-react-parser';
import type { ReactNode } from 'react';

/**
 * Sanitizes an HTML string using DOMPurify to prevent XSS.
 * Use when you need a clean HTML string (e.g. for further processing).
 * Prefer parseHtml() when rendering in JSX.
 */
export function sanitizeHtml(html: string | null | undefined): string {
    // ADD_ATTR: ['target'] preserves target="_blank" so the noopener/noreferrer
    // hardening in parseHtml can actually fire (DOMPurify strips target by default).
    return DOMPurify.sanitize(html ?? '', { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] });
}

/**
 * Sanitizes and parses an HTML string into React elements.
 * Use this instead of dangerouslySetInnerHTML to safely render server-provided rich-text HTML.
 *
 * @example
 * // Before:
 * <div dangerouslySetInnerHTML={{ __html: someHtml }} />
 *
 * // After:
 * <div>{parseHtml(someHtml)}</div>
 */
export function parseHtml(html: string | null | undefined): ReactNode {
    const options: HTMLReactParserOptions = {
        replace: (node: DOMNode) => {
            // Hardening for links: always force rel="noopener noreferrer" on target=_blank links.
            // We intentionally ignore any existing rel to prevent reverse tabnabbing
            // (an attacker-supplied rel="opener" would otherwise override noopener).
            if ((node as Element).type === 'tag' && (node as Element).name === 'a') {
                const el = node as Element;
                const attribs = el.attribs ?? {};
                const safeAttribs = { ...attribs };

                // Block javascript:, data:, and vbscript: URIs in href for ALL anchor elements.
                const href = safeAttribs.href;
                if (href && /^\s*(javascript|data|vbscript):/i.test(href)) {
                    delete safeAttribs.href;
                }

                const target = safeAttribs.target;
                if (target?.toLowerCase() === '_blank') {
                    // Always drop any attacker-supplied rel to prevent reverse tabnabbing.
                    delete safeAttribs.rel;
                    delete safeAttribs.target;
                    // attributesToProps maps HTML attribute names to correct React prop names
                    // (e.g. class → className, tabindex → tabIndex) and avoids React warnings.
                    const props = attributesToProps(safeAttribs);

                    return (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                            {domToReact(el.children as DOMNode[], options)}
                        </a>
                    );
                }

                // Re-render all other anchor elements so the sanitized href takes effect.
                const props = attributesToProps(safeAttribs);
                return <a {...props}>{domToReact(el.children as DOMNode[], options)}</a>;
            }

            return undefined;
        },
    };

    return parse(sanitizeHtml(html), options);
}
